import { create } from 'zustand';
import type { CodeSnapshot } from '../services/hitl';

export interface CanonicalLearningItem {
  id: string;
  type: 'lecture' | 'reading' | 'lab' | 'assessment';
  title: string;
  videoExternalId?: string | null;
  labId?: string | null;
}

export type CanonicalSnapshotReader = () => CodeSnapshot | undefined;

interface CanonicalLearningContext {
  ownerId: string;
  courseSlug: string;
  item: CanonicalLearningItem | null;
  snapshotItemId: string | null;
  snapshotReader: CanonicalSnapshotReader | null;
}

interface CanonicalLearningContextState {
  context: CanonicalLearningContext | null;
  claim: (ownerId: string, courseSlug: string) => void;
  publishItem: (
    ownerId: string,
    courseSlug: string,
    item: CanonicalLearningItem | null,
  ) => void;
  publishWorkspace: (
    ownerId: string,
    itemId: string,
    reader: CanonicalSnapshotReader,
  ) => void;
  clearWorkspace: (
    ownerId: string,
    itemId: string,
    reader: CanonicalSnapshotReader,
  ) => void;
  release: (ownerId: string) => void;
}

let ownerSequence = 0;

export function createCanonicalContextOwner(): string {
  ownerSequence += 1;
  return `canonical-shell-${ownerSequence}`;
}

/**
 * Authoritative context for the active `/portal/course` or `/portal/lab`
 * shell. Ownership makes route cleanup compare-and-clear: an old shell or
 * workspace effect cannot erase or republish over a newer route.
 */
export const useCanonicalLearningContextStore =
  create<CanonicalLearningContextState>((set) => ({
    context: null,

    claim: (ownerId, courseSlug) =>
      set({
        context: {
          ownerId,
          courseSlug,
          item: null,
          snapshotItemId: null,
          snapshotReader: null,
        },
      }),

    publishItem: (ownerId, courseSlug, item) =>
      set((state) => {
        const current = state.context;
        if (
          !current ||
          current.ownerId !== ownerId ||
          current.courseSlug !== courseSlug
        ) {
          return state;
        }
        const itemChanged = current.item?.id !== item?.id;
        return {
          context: {
            ...current,
            item,
            snapshotItemId: itemChanged ? null : current.snapshotItemId,
            snapshotReader: itemChanged ? null : current.snapshotReader,
          },
        };
      }),

    publishWorkspace: (ownerId, itemId, reader) =>
      set((state) => {
        const current = state.context;
        if (
          !current ||
          current.ownerId !== ownerId ||
          current.item?.id !== itemId ||
          current.item.type !== 'lab'
        ) {
          return state;
        }
        return {
          context: {
            ...current,
            snapshotItemId: itemId,
            snapshotReader: reader,
          },
        };
      }),

    clearWorkspace: (ownerId, itemId, reader) =>
      set((state) => {
        const current = state.context;
        if (
          !current ||
          current.ownerId !== ownerId ||
          current.snapshotItemId !== itemId ||
          current.snapshotReader !== reader
        ) {
          return state;
        }
        return {
          context: {
            ...current,
            snapshotItemId: null,
            snapshotReader: null,
          },
        };
      }),

    release: (ownerId) =>
      set((state) =>
        state.context?.ownerId === ownerId ? { context: null } : state,
      ),
  }));

export interface CompanionHitlContext {
  canonical: boolean;
  sourceContentId?: string;
  getCodeSnapshot: () => CodeSnapshot | undefined;
}

/**
 * Resolve HITL context without allowing legacy stores to win while a canonical
 * portal shell owns the route. The snapshot reader is evaluated lazily and
 * re-checks the live owner/item pair before returning any code.
 */
export function resolveCompanionHitlContext(
  legacySourceContentId: string | undefined,
  readLegacyCodeSnapshot: () => CodeSnapshot | undefined,
): CompanionHitlContext {
  const initial = useCanonicalLearningContextStore.getState().context;
  if (!initial) {
    return {
      canonical: false,
      sourceContentId: legacySourceContentId,
      getCodeSnapshot: readLegacyCodeSnapshot,
    };
  }

  const ownerId = initial.ownerId;
  const itemId = initial.item?.id;
  return {
    canonical: true,
    sourceContentId: itemId,
    getCodeSnapshot: () => {
      const current = useCanonicalLearningContextStore.getState().context;
      if (
        !current ||
        current.ownerId !== ownerId ||
        current.item?.id !== itemId ||
        current.snapshotItemId !== itemId
      ) {
        return undefined;
      }
      return current.snapshotReader?.();
    },
  };
}
